"use client";

import { Fragment } from "react";
import classNames from "classnames";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Tab,
  TabGroup,
  TabList,
  Transition,
} from "@headlessui/react";
import { useScrollSelectedIntoView } from "~/hooks/useScrollSelectedIntoView";
import { HiChevronDown } from "react-icons/hi";
import {
  MOBILE_TAB_LISTBOX_BUTTON_CLASSES_WHITE,
  MOBILE_TAB_LISTBOX_CHEVRON_ICON_CLASSES,
  MOBILE_TAB_LISTBOX_CHEVRON_WRAP_CLASSES,
  MOBILE_TAB_LISTBOX_OPTIONS_CLASSES_WHITE,
  MOBILE_TAB_LISTBOX_TRANSITION_PROPS,
  SCROLL_CONTAINER_CLASS,
  TAB_BORDER_LINE_CLASSES,
  mobileTabListboxOptionClassName,
} from "~/utils/tabstyles";
import React from "react";

// ─── Types ─────────────────────────────────────────────────────────────────

type TabItem = { label: string };

// ─── Constants ──────────────────────────────────────────────────────────────

/** Profile tabs: compact at md (768–931px), full size at lg+ */
const TAB_CLASSES_HEADLESS_PROFILE = [
  "tw:flex! tw:items-center! tw:justify-center! tw:gap-1! tw:lg:gap-2! tw:min-w-[100px]! tw:lg:min-w-[140px]! tw:shrink-0! tw:rounded-t-lg! tw:rounded-b-none!",
  "tw:border-2! tw:border-gray-400! tw:border-b-night-800!",
  "tw:outline-none! tw:whitespace-nowrap! tw:transition-colors! tw:duration-150! tw:ease-out!",
  "tw:text-lg! tw:font-medium! tw:leading-6! tw:text-gray-700! tw:tracking-tight!",
  "tw:pt-2! tw:px-4! tw:pb-1.5! tw:md:px-3! tw:lg:px-6!",
  "tw:hover:font-semibold! tw:hover:text-night-900!",
  "tw:data-[selected]:font-semibold! tw:data-[selected]:text-cn-dark-blue!",
  "tw:data-[selected]:border-2! tw:data-[selected]:border-x-night-800! tw:data-[selected]:border-t-night-800!",
  "tw:data-[selected]:border-b-2! tw:data-[selected]:border-b-white! tw:data-[selected]:bg-white!",
  "tw:data-[focus]:outline-none! tw:data-[focus]:ring-0! tw:data-[active]:outline-none! tw:data-[active]:ring-0!",
].join(" ");

// ─── MobileDropdown ────────────────────────────────────────────────────────

interface MobileDropdownProps {
  tabData: TabItem[];
  selectedTab: TabItem | undefined;
  onSelect: (tab: TabItem) => void;
}

function MobileDropdown({
  tabData,
  selectedTab,
  onSelect,
}: MobileDropdownProps) {
  return (
    <div className="tw:relative tw:w-full tw:md:hidden">
      <Listbox
        value={selectedTab}
        onChange={onSelect}
        aria-label="Profile navigation"
      >
        {({ open }) => (
          <div className="tw:relative">
            <ListboxButton
              className={MOBILE_TAB_LISTBOX_BUTTON_CLASSES_WHITE}
              aria-expanded={open}
              aria-haspopup="listbox"
              aria-label="Select profile section"
            >
              <span className="tw:block tw:truncate tw:text-left">
                {selectedTab?.label ?? "Select section"}
              </span>
              <span
                className={classNames(MOBILE_TAB_LISTBOX_CHEVRON_WRAP_CLASSES, {
                  "tw:rotate-180": open,
                })}
                aria-hidden
              >
                <HiChevronDown
                  className={MOBILE_TAB_LISTBOX_CHEVRON_ICON_CLASSES}
                />
              </span>
            </ListboxButton>
            <Transition
              show={open}
              as={Fragment}
              {...MOBILE_TAB_LISTBOX_TRANSITION_PROPS}
            >
              <ListboxOptions
                className={MOBILE_TAB_LISTBOX_OPTIONS_CLASSES_WHITE}
                aria-label="Profile sections"
              >
                {tabData.map((tab) => (
                  <ListboxOption
                    key={tab.label}
                    value={tab}
                    className={({ focus, selected }) =>
                      mobileTabListboxOptionClassName({ focus, selected })
                    }
                  >
                    {tab.label}
                  </ListboxOption>
                ))}
              </ListboxOptions>
            </Transition>
          </div>
        )}
      </Listbox>
    </div>
  );
}

export function Tabs({
  tabData,
  onChange,
}: {
  tabData: TabItem[];
  onChange?: (index: number) => void;
}) {
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const scrollContainerRef = useScrollSelectedIntoView(
    selectedIndex.toString(),
  );
  const selectedTab = tabData[selectedIndex];

  const handleTabChange = (index: number) => {
    setSelectedIndex(index);
    onChange?.(index);
  };

  const handleMobileSelect = (tab: TabItem) => {
    const newIndex = tabData.findIndex((t) => t.label === tab.label);
    handleTabChange(newIndex);
  };

  return (
    <nav
      className="tw:relative tw:z-10 tw:w-full tw:min-w-0 tw:isolate tw:pb-3 tw:md:pb-0"
      aria-label="Profile navigation"
    >
      <MobileDropdown
        tabData={tabData}
        selectedTab={selectedTab}
        onSelect={handleMobileSelect}
      />

      <TabGroup
        selectedIndex={selectedIndex}
        onChange={(index) => {
          handleTabChange(index);
        }}
        manual
      >
        <div
          ref={scrollContainerRef}
          className={classNames(
            SCROLL_CONTAINER_CLASS,
            "tw:hidden tw:md:block tw:md:pt-8 tw:lg:pt-0",
          )}
        >
          <TabList
            className="tw:flex tw:items-end tw:gap-1 tw:lg:gap-2"
            aria-label="Profile sections"
          >
            {tabData.map(({ label }, index) => (
              <Tab
                key={index.toString()}
                className={classNames(
                  TAB_CLASSES_HEADLESS_PROFILE,
                  "tw:bg-white!",
                )}
              >
                {label}
              </Tab>
            ))}
          </TabList>
        </div>
      </TabGroup>

      <div className={TAB_BORDER_LINE_CLASSES} aria-hidden />
    </nav>
  );
}
